import {
  PortalDsButton,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { formatDisplayDateTime } from 'src/helpers/date.helpers';
import { formatScheduleForDisplay } from 'src/helpers/scheduler-schedule.helpers';
import { formatRowCount } from '../helpers/data-transfers.helpers';
import type { ClientDataTransfer, DataTransferRun } from '../types/data-transfers.types';
import { DataTransferRunHistory } from './data-transfer-run-history';
import { DataTransferStatusBadge } from './data-transfer-status-badge';
import { SqlEditor } from './sql-editor';

interface ClientDataTransferDetailSheetProps {
  open: boolean;
  transfer: ClientDataTransfer | null;
  onOpenChange: (open: boolean) => void;
  onEdit: (transfer: ClientDataTransfer) => void;
  onSelectRun: (run: DataTransferRun) => void;
}

export function ClientDataTransferDetailSheet({
  open,
  transfer,
  onOpenChange,
  onEdit,
  onSelectRun,
}: ClientDataTransferDetailSheetProps) {
  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:!w-[70vw] sm:!max-w-none"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {transfer?.name ?? 'Transfer detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              Review SQL, schedule, and run history for this client export.
            </SheetDescription>
          </SheetHeader>
        </div>

        {transfer ? (
          <div className="flex-1 space-y-8 px-6 py-5">
            <section className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                  <DataTransferStatusBadge
                    status={transfer.last_status}
                    disabled={!transfer.is_enabled}
                  />
                  <span className={portalDs.bodySm}>
                    {transfer.client_title}
                  </span>
                </div>
                <PortalDsButton
                  type="button"
                  variant="ghost"
                  onClick={() => onEdit(transfer)}
                >
                  Edit transfer
                </PortalDsButton>
              </div>

              {transfer.description ? (
                <p className={portalDs.bodySm}>
                  {transfer.description}
                </p>
              ) : null}

              <dl className="grid gap-4 sm:grid-cols-2">
                <div>
                  <dt className={portalDs.label}>
                    Schedule
                  </dt>
                  <dd className="font-mono text-xs text-gray-700">
                    {formatScheduleForDisplay(transfer)}
                  </dd>
                </div>
                <div>
                  <dt className={portalDs.label}>
                    Last run
                  </dt>
                  <dd className={portalDs.bodySm}>
                    {transfer.last_run_at
                      ? formatDisplayDateTime(transfer.last_run_at)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className={portalDs.label}>
                    Next run
                  </dt>
                  <dd className={portalDs.bodySm}>
                    {transfer.is_enabled && transfer.next_run_at
                      ? formatDisplayDateTime(transfer.next_run_at)
                      : '—'}
                  </dd>
                </div>
                <div>
                  <dt className={portalDs.label}>
                    Last row count
                  </dt>
                  <dd className={portalDs.bodySm}>
                    {formatRowCount(transfer.last_row_count)}
                  </dd>
                </div>
              </dl>

              <div className="space-y-2">
                <p className={portalDs.label}>
                  SQL
                </p>
                <SqlEditor
                  value={transfer.sql}
                  readOnly
                  height={280}
                  onChange={() => undefined}
                />
              </div>
            </section>

            <section className="space-y-4">
              <h3 className={portalDs.sectionTitle}>
                Run history
              </h3>
              <DataTransferRunHistory
                transfer={transfer}
                onSelectRun={onSelectRun}
              />
            </section>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
